import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import usePersistedState from '../hooks/usePersistedState';
import AnalysisProgress from './AnalysisProgress';

function Point2({ projectId }) {
  const [documents, setDocuments] = usePersistedState(projectId, 'documents', []);

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisProgressData, setAnalysisProgressData] = useState(null); 

  const [companyName, setCompanyName] = usePersistedState(projectId, 'companyName', '');
  const [companySize, setCompanySize] = usePersistedState(projectId, 'companySize', '');
  const [sector, setSector] = usePersistedState(projectId, 'sector', '');
  const [products, setProducts] = usePersistedState(projectId, 'products', '');
  const [location, setLocation] = usePersistedState(projectId, 'location', '');
  const [additionalTerms, setAdditionalTerms] = usePersistedState(projectId, 'additionalTerms', '');
  const [priorityUrls, setPriorityUrls] = usePersistedState(projectId, 'priorityUrls', []);
  const [newUrl, setNewUrl] = useState('');
  const [analysisType, setAnalysisType] = usePersistedState(projectId, 'analysisType', 'Información general');
  const [prompt, setPrompt] = useState('');
  const [searchMode, setSearchMode] = usePersistedState(projectId, 'searchMode', 'Navegación profunda');

  const [activeAnalysisTab, setActiveAnalysisTab] = usePersistedState(projectId, 'activeAnalysisTab', 0);
  const [selectedDocuments, setSelectedDocuments] = usePersistedState(projectId, 'selectedDocuments', []);
  const [ragResults, setRagResults] = usePersistedState(projectId, 'ragResults', null);
  const [webResults, setWebResults] = usePersistedState(projectId, 'webResults', null);
  const [docsResults, setDocsResults] = usePersistedState(projectId, 'docsResults', null);
  const [integratedResults, setIntegratedResults] = usePersistedState(projectId, 'integratedResults', null);
  const [selectedSources, setSelectedSources] = usePersistedState(projectId, 'selectedSources', {
    documents: false,
    rag: false,
    web: false,
  });

  const resetUploadState = () => {
    setUploadLoading(false);
    setUploadStatus('');
    setUploadProgress(0);
  };

  const resetAnalysisState = () => {
    setAnalysisLoading(false);
    setAnalysisStatus('');
    setAnalysisProgress(0);
  };

  const companySizeOptions = [
    'Startup/Pequeña (1-10 empleados)',
    'Pequeña (11-50 empleados)',
    'Mediana (51-250 empleados)',
    'Grande (251-1000 empleados)',
    'Muy Grande (1000+ empleados)',
  ];

  const sectorOptions = [
    'Tecnología avanzada',
    'Servicios financieros',
    'Manufactura',
    'Retail/Comercio',
    'Salud y farmacéutico',
    'Educación',
    'Energía',
    'Construcción',
    'Logística y transporte',
    'Turismo y hostelería',
  ];

  const analysisTypeOptions = [
    'Información general',
    'Análisis competitivo',
    'Análisis financiero',
    'Estrategia de mercado',
    'Análisis DAFO',
    'Modelo de negocio',
  ];

  const promptTemplates = {
    'Información general': `Analiza la información de la empresa y proporciona:
- Historia y fundación de la empresa
- Misión, visión y valores corporativos
- Principales productos/servicios
- Estructura organizativa
- Presencia geográfica actual
- Logros y reconocimientos destacados`,

    'Análisis competitivo': `Realiza un análisis competitivo detallado:
- Identificación de competidores directos e indirectos
- Posicionamiento en el mercado
- Ventajas competitivas diferenciales
- Cuota de mercado estimada
- Fortalezas y debilidades vs competencia
- Estrategias competitivas recomendadas`,

    'Análisis financiero': `Analiza la situación financiera de la empresa:
- Ingresos y rentabilidad
- Estructura de costes
- Flujo de caja y liquidez
- Nivel de endeudamiento
- Ratios financieros clave
- Tendencias y proyecciones`,

    'Estrategia de mercado': `Evalúa la estrategia de mercado:
- Segmentación de mercado objetivo
- Propuesta de valor única
- Canales de distribución
- Estrategia de precios
- Posicionamiento de marca
- Plan de expansión`,

    'Análisis DAFO': `Desarrolla un análisis DAFO completo:
- Debilidades internas identificadas
- Amenazas del entorno externo
- Fortalezas y capacidades clave
- Oportunidades de mercado
- Estrategias derivadas del análisis
- Recomendaciones de acción`,

    'Modelo de negocio': `Analiza el modelo de negocio:
- Canvas del modelo de negocio
- Flujos de ingresos principales
- Estructura de costes
- Propuesta de valor
- Segmentos de clientes
- Canales y relaciones con clientes`,
  };

  const analysisTabOptions = ['Documentos', 'Base Conocimiento', 'Análisis Web', 'Resumen Integrado'];

  useEffect(() => {
    if (documents.length === 0) {
      fetchDocuments();
    }
  }, [projectId]); // intencional: cargar al cambiar de proyecto

  useEffect(() => {
    setPrompt(promptTemplates[analysisType] || '');
  }, [analysisType]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`/api/projects/${projectId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploadLoading(true);
    setUploadStatus(`Subiendo ${file.name}...`);
    setUploadProgress(10);

    try {
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const uploadProgress = Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setUploadStatus(`Subiendo ${file.name}... ${uploadProgress + 10}%`);
          setUploadProgress(10 + uploadProgress);
        },
      };

      setUploadStatus('Procesando archivo...');
      setUploadProgress(65);

      await axios.post(`/api/projects/${projectId}/documents`, formData, config);

      setUploadStatus('Extrayendo texto...');
      setUploadProgress(80);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setUploadStatus('Guardando en base de datos...');
      setUploadProgress(95);
      await fetchDocuments();

      setUploadStatus('Documento procesado correctamente');
      setUploadProgress(100);

      setTimeout(resetUploadState, 1000);
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus('Error al procesar documento');
      setTimeout(resetUploadState, 2000);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este documento?')) {
      return;
    }

    try {
      await axios.delete(`/api/projects/${projectId}/documents/${documentId}`);
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error al eliminar el documento');
    }
  };

  const addPriorityUrl = () => {
    if (newUrl.trim() && !priorityUrls.includes(newUrl.trim())) {
      setPriorityUrls([...priorityUrls, newUrl.trim()]);
      setNewUrl('');
    }
  };

  const removePriorityUrl = (url) => {
    setPriorityUrls(priorityUrls.filter((u) => u !== url));
  };

  const handleDocumentSelection = (docId) => {
    if (selectedDocuments.includes(docId)) {
      setSelectedDocuments(selectedDocuments.filter((id) => id !== docId));
    } else {
      setSelectedDocuments([...selectedDocuments, docId]);
    }
  };

  const performDocumentAnalysis = async () => {
    if (selectedDocuments.length === 0) {
      alert('Selecciona al menos un documento para analizar');
      return;
    }

    setAnalysisLoading(true);
    setAnalysisStatus('Analizando documentos seleccionados...');
    setAnalysisProgress(50);

    try {
      const requestData = {
        project_id: projectId,
        point_number: 2,
        prompt: `${prompt}\n\nEMPRESA: ${companyName}\nSECTOR: ${sector}\nUBICACIÓN: ${location}`,
        use_web: false,
        search_terms: '',
        web_only: false,
        selected_documents: selectedDocuments,
      };

      const response = await axios.post('/api/analysis/', requestData);

      setDocsResults(response.data);
      setAnalysisStatus('Análisis completado');
      setAnalysisProgress(100);

      setTimeout(resetAnalysisState, 1000);
    } catch (error) {
      console.error('Error analyzing documents:', error);
      setAnalysisStatus('Error en análisis');
      setTimeout(resetAnalysisState, 2000);
    }
  };

  const performRagAnalysis = async () => {
    setAnalysisLoading(true);
    setAnalysisStatus('Consultando base de conocimiento...');
    setAnalysisProgress(50);

    try {
      const requestData = {
        project_id: projectId,
        point_number: 2,
        prompt: `${prompt}\n\nEMPRESA: ${companyName}\nSECTOR: ${sector}\nUBICACIÓN: ${location}`,
        use_rag: true,
        search_terms: `${companyName} ${sector} ${products}`.trim(),
      };

      const response = await axios.post('/api/analysis/', requestData);

      setRagResults(response.data);
      setAnalysisStatus('Análisis completado');
      setAnalysisProgress(100);

      setTimeout(resetAnalysisState, 1000);
    } catch (error) {
      console.error('Error in RAG analysis:', error);
      setAnalysisStatus('Error en análisis RAG');
      setTimeout(resetAnalysisState, 2000);
    }
  };

  const performWebAnalysis = async () => {
    if (!companyName.trim()) {
      alert('Por favor, introduce el nombre de la empresa para la búsqueda web');
      return;
    }

    setAnalysisLoading(true);
    setAnalysisProgressData({ status: 'Iniciando análisis...', progress: 5, phase: 'starting' });
    
    const timeoutDuration = priorityUrls.length > 0 ? 600000 : 120000;

    try {
      const requestData = {
        project_id: projectId,
        point_number: 2,
        prompt: `${prompt}\n\nEMPRESA: ${companyName}\nSECTOR: ${sector}\nUBICACIÓN: ${location}`,
        use_web: true,
        search_terms: [companyName, sector, products, location, additionalTerms].filter(Boolean).join(' '),
        web_only: true,
        priority_urls: priorityUrls.length > 0 ? priorityUrls : null,
        use_deep_crawling: priorityUrls.length > 0,
      };

      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), timeoutDuration)
      );

      const startResponse = await axios.post('/api/analysis/start', requestData);
      const analysisId = startResponse.data.analysis_id;

      const checkStatus = async () => {
        const status = await axios.get(`/api/analysis/${analysisId}/status`);
        
        if (status.data.completed) {
          return status.data.result;
        }
        
        setAnalysisProgressData(status.data);
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        return checkStatus();
      };

      const response = await Promise.race([checkStatus(), timeoutPromise]);
      
      setWebResults(response);
      setAnalysisProgressData({ status: 'Análisis completado', progress: 100, phase: 'completed' });
    } catch (error) {
      if (error.message === 'Timeout') {
        setAnalysisProgressData({ status: 'Análisis muy largo, revisa resultados más tarde', progress: 0, phase: 'timeout' });
      } else {
        setAnalysisProgressData({ status: 'Error en análisis', progress: 0, phase: 'error' });
      }
    } finally {
      setTimeout(() => {
        setAnalysisLoading(false);
        setAnalysisProgressData(null);
      }, 2000);
    }
  };

  const performIntegratedAnalysis = async () => {
    const selectedCount = Object.values(selectedSources).filter(Boolean).length;
    if (selectedCount === 0) {
      alert('Selecciona al menos una fuente para el resumen integrado');
      return;
    }

    setAnalysisLoading(true);
    setAnalysisStatus('Generando resumen integrado...');
    setAnalysisProgress(50);

    try {
      const sourcesData = [];
      if (selectedSources.documents && docsResults) sourcesData.push(docsResults);
      if (selectedSources.rag && ragResults) sourcesData.push(ragResults);
      if (selectedSources.web && webResults) sourcesData.push(webResults);

      const requestData = {
        project_id: projectId,
        point_number: 2,
        prompt: `${prompt}\n\nGenera un resumen integrado basado en las siguientes fuentes:\n\nEMPRESA: ${companyName}\nSECTOR: ${sector}\nUBICACIÓN: ${location}`,
        integrated_sources: sourcesData,
        analysis_type: 'integrated',
      };

      const response = await axios.post('/api/analysis/', requestData);

      setIntegratedResults(response.data);
      setAnalysisStatus('Análisis completado');
      setAnalysisProgress(100);

      setTimeout(resetAnalysisState, 1000);
    } catch (error) {
      console.error('Error in integrated analysis:', error);
      setAnalysisStatus('Error en análisis integrado');
      setTimeout(resetAnalysisState, 2000);
    }
  };

  const renderAnalysisTabContent = () => {
    switch (activeAnalysisTab) {
      case 0:
        return (
          <div className="analysis-tab-content">
            <h5>Seleccionar documentos para análisis:</h5>
            {documents.length === 0 ? (
              <p>No hay documentos disponibles. Sube documentos primero.</p>
            ) : (
              <div className="documents-selector">
                {documents.map((doc) => (
                  <label key={doc.id} className="document-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDocuments.includes(doc.id)}
                      onChange={() => handleDocumentSelection(doc.id)}
                    />
                    <span>📄 {doc.filename}</span>
                  </label>
                ))}
                <button
                  onClick={performDocumentAnalysis}
                  disabled={analysisLoading || selectedDocuments.length === 0}
                  className="analyze-btn primary"
                >
                  {analysisLoading ? `${analysisStatus} (${analysisProgress}%)` : 'Analizar Documentos Seleccionados'}
                </button>

                {analysisLoading && (
                  <div className="loading-indicator" style={{ marginTop: '15px' }}>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${analysisProgress}%` }} />
                    </div>
                    <span className="loading-text">{analysisStatus}</span>
                  </div>
                )}
              </div>
            )}

            {docsResults && (
              <div
                className="analysis-result"
                style={{
                  marginTop: '30px',
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                }}
              >
                <h4 style={{ marginBottom: '20px' }}>Resultado del Análisis de Documentos</h4>
                <div className="result-content" style={{ lineHeight: '1.2', fontSize: '14px' }}>
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p style={{ marginBottom: '6px', lineHeight: '1.2' }}>{children}</p>,
                      h1: ({ children }) => (
                        <h1 style={{ marginBottom: '8px', marginTop: '12px', fontSize: '20px' }}>{children}</h1>
                      ),
                      h2: ({ children }) => (
                        <h2 style={{ marginBottom: '6px', marginTop: '10px', fontSize: '18px' }}>{children}</h2>
                      ),
                      h3: ({ children }) => (
                        <h3 style={{ marginBottom: '6px', marginTop: '8px', fontSize: '16px' }}>{children}</h3>
                      ),
                      ul: ({ children }) => <ul style={{ marginBottom: '6px', paddingLeft: '20px' }}>{children}</ul>,
                      ol: ({ children }) => <ol style={{ marginBottom: '6px', paddingLeft: '20px' }}>{children}</ol>,
                      li: ({ children }) => <li style={{ marginBottom: '2px', lineHeight: '1.2' }}>{children}</li>,
                    }}
                  >
                    {docsResults.content}
                  </ReactMarkdown>
                </div>
                {docsResults.sources && docsResults.sources.length > 0 && (
                  <div className="sources" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                    <h5>Fuentes utilizadas:</h5>
                    <ul>
                      {docsResults.sources.map((source, index) => (
                        <li key={index}>{source}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 1:
        return (
          <div className="analysis-tab-content">
            <h5>Análisis de Base de Conocimiento (RAG):</h5>
            <p>Consulta la documentación corporativa almacenada en la base de conocimiento.</p>
            <button onClick={performRagAnalysis} disabled={analysisLoading} className="analyze-btn primary">
              {analysisLoading ? `${analysisStatus} (${analysisProgress}%)` : 'Consultar Base de Conocimiento'}
            </button>

            {analysisLoading && (
              <div className="loading-indicator" style={{ marginTop: '15px' }}>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${analysisProgress}%` }} />
                </div>
                <span className="loading-text">{analysisStatus}</span>
              </div>
            )}

            {ragResults && (
              <div
                className="analysis-result"
                style={{
                  marginTop: '30px',
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                }}
              >
                <h4 style={{ marginBottom: '20px' }}>Resultado del Análisis RAG</h4>
                <div className="result-content" style={{ lineHeight: '1.2', fontSize: '14px' }}>
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p style={{ marginBottom: '6px', lineHeight: '1.2' }}>{children}</p>,
                      h1: ({ children }) => (
                        <h1 style={{ marginBottom: '8px', marginTop: '12px', fontSize: '20px' }}>{children}</h1>
                      ),
                      h2: ({ children }) => (
                        <h2 style={{ marginBottom: '6px', marginTop: '10px', fontSize: '18px' }}>{children}</h2>
                      ),
                      h3: ({ children }) => (
                        <h3 style={{ marginBottom: '6px', marginTop: '8px', fontSize: '16px' }}>{children}</h3>
                      ),
                      ul: ({ children }) => <ul style={{ marginBottom: '6px', paddingLeft: '20px' }}>{children}</ul>,
                      ol: ({ children }) => <ol style={{ marginBottom: '6px', paddingLeft: '20px' }}>{children}</ol>,
                      li: ({ children }) => <li style={{ marginBottom: '2px', lineHeight: '1.2' }}>{children}</li>,
                    }}
                  >
                    {ragResults.content}
                  </ReactMarkdown>
                </div>
                {ragResults.sources && ragResults.sources.length > 0 && (
                  <div className="sources" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                    <h5>Fuentes utilizadas:</h5>
                    <ul>
                      {ragResults.sources.map((source, index) => (
                        <li key={index}>{source}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="analysis-tab-content">
            <div className="priority-urls">
              <h5>URLS PRIORITARIAS. (Opcional).</h5>
              <p>Añade páginas web específicas para que el sistema las analice primero.</p>

              <div className="url-input" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <input
                  type="text"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="www.empresa.com/nosotros"
                  style={{ flex: 1 }}
                />
                <button onClick={addPriorityUrl} type="button">
                  Añadir
                </button>
              </div>

              {priorityUrls.length > 0 && (
                <div className="urls-list" style={{ marginBottom: '20px' }}>
                  {priorityUrls.map((url, index) => (
                    <div
                      key={index}
                      className="url-item"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        margin: '5px 0',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6',
                      }}
                    >
                      <span>{url}</span>
                      <button
                        onClick={() => removePriorityUrl(url)}
                        type="button"
                        style={{
                          color: 'red',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          padding: '0 5px',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* <div className="mode-options" style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
              <div
                className={`mode-card ${searchMode === 'Navegación profunda' ? 'active' : ''}`}
                onClick={() => setSearchMode('Navegación profunda')}
                style={{ flex: 1 }}
              >
                <h5>Navegación profunda</h5>
                <p>Sólo las páginas que añadas.</p>
              </div>

              <div
                className={`mode-card ${searchMode === 'Investigación inteligente' ? 'active' : ''}`}
                onClick={() => setSearchMode('Investigación inteligente')}
                style={{ flex: 1 }}
              >
                <h5>Investigación inteligente</h5>
                <p>URLs opcionales, búsqueda automática, APIs avanzadas.</p>
              </div>
            </div> */}

            <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '30px' }}>
              <button
                onClick={performWebAnalysis}
                disabled={analysisLoading || !companyName.trim()}
                className="analyze-btn primary"
              >
                {analysisLoading ? `Realizando Análisis Web` : 'Realizar Análisis Web'}
              </button>
            </div>

            {analysisLoading && analysisProgressData && (
              <AnalysisProgress analysisData={analysisProgressData} />
            )}

            {webResults && (
              <div
                className="analysis-result"
                style={{
                  marginTop: '30px',
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                }}
              >
                <h4 style={{ marginBottom: '20px' }}>Resultado del Análisis Web</h4>
                <div className="result-content" style={{ lineHeight: '1.2', fontSize: '14px' }}>
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p style={{ marginBottom: '6px', lineHeight: '1.2' }}>{children}</p>,
                      h1: ({ children }) => (
                        <h1 style={{ marginBottom: '8px', marginTop: '12px', fontSize: '20px' }}>{children}</h1>
                      ),
                      h2: ({ children }) => (
                        <h2 style={{ marginBottom: '6px', marginTop: '10px', fontSize: '18px' }}>{children}</h2>
                      ),
                      h3: ({ children }) => (
                        <h3 style={{ marginBottom: '6px', marginTop: '8px', fontSize: '16px' }}>{children}</h3>
                      ),
                      ul: ({ children }) => <ul style={{ marginBottom: '6px', paddingLeft: '20px' }}>{children}</ul>,
                      ol: ({ children }) => <ol style={{ marginBottom: '6px', paddingLeft: '20px' }}>{children}</ol>,
                      li: ({ children }) => <li style={{ marginBottom: '2px', lineHeight: '1.2' }}>{children}</li>,
                    }}
                  >
                    {webResults.content}
                  </ReactMarkdown>
                </div>
                {webResults.sources && webResults.sources.length > 0 && (
                  <div className="sources" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                    <h5>Fuentes utilizadas:</h5>
                    <ul>
                      {webResults.sources.map((source, index) => (
                        <li key={index}>{source}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="analysis-tab-content">
            <h5>Seleccionar fuentes para resumen integrado:</h5>
            <div className="sources-selector">
              <label className="source-checkbox">
                <input
                  type="checkbox"
                  checked={selectedSources.documents}
                  onChange={(e) => setSelectedSources({ ...selectedSources, documents: e.target.checked })}
                  disabled={!docsResults}
                />
                <span>Análisis de Documentos {docsResults ? '✓' : '(No disponible)'}</span>
              </label>

              <label className="source-checkbox">
                <input
                  type="checkbox"
                  checked={selectedSources.rag}
                  onChange={(e) => setSelectedSources({ ...selectedSources, rag: e.target.checked })}
                  disabled={!ragResults}
                />
                <span>Base de Conocimiento {ragResults ? '✓' : '(No disponible)'}</span>
              </label>

              <label className="source-checkbox">
                <input
                  type="checkbox"
                  checked={selectedSources.web}
                  onChange={(e) => setSelectedSources({ ...selectedSources, web: e.target.checked })}
                  disabled={!webResults}
                />
                <span>Análisis Web {webResults ? '✓' : '(No disponible)'}</span>
              </label>
            </div>

            <button
              onClick={performIntegratedAnalysis}
              disabled={analysisLoading || Object.values(selectedSources).filter(Boolean).length === 0}
              className="analyze-btn primary"
            >
              {analysisLoading ? `${analysisStatus} (${analysisProgress}%)` : 'Generar Resumen Integrado'}
            </button>

            {analysisLoading && (
              <div className="loading-indicator" style={{ marginTop: '15px' }}>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${analysisProgress}%` }} />
                </div>
                <span className="loading-text">{analysisStatus}</span>
              </div>
            )}

            {integratedResults && (
              <div
                className="analysis-result"
                style={{
                  marginTop: '30px',
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                }}
              >
                <h4 style={{ marginBottom: '20px' }}>Resumen Integrado</h4>
                <div className="result-content" style={{ lineHeight: '1.2', fontSize: '14px' }}>
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p style={{ marginBottom: '6px', lineHeight: '1.2' }}>{children}</p>,
                      h1: ({ children }) => (
                        <h1 style={{ marginBottom: '8px', marginTop: '12px', fontSize: '20px' }}>{children}</h1>
                      ),
                      h2: ({ children }) => (
                        <h2 style={{ marginBottom: '6px', marginTop: '10px', fontSize: '18px' }}>{children}</h2>
                      ),
                      h3: ({ children }) => (
                        <h3 style={{ marginBottom: '6px', marginTop: '8px', fontSize: '16px' }}>{children}</h3>
                      ),
                      ul: ({ children }) => <ul style={{ marginBottom: '6px', paddingLeft: '20px' }}>{children}</ul>,
                      ol: ({ children }) => <ol style={{ marginBottom: '6px', paddingLeft: '20px' }}>{children}</ol>,
                      li: ({ children }) => <li style={{ marginBottom: '2px', lineHeight: '1.2' }}>{children}</li>,
                    }}
                  >
                    {integratedResults.content}
                  </ReactMarkdown>
                </div>
                {integratedResults.sources && integratedResults.sources.length > 0 && (
                  <div className="sources" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                    <h5>Fuentes utilizadas:</h5>
                    <ul>
                      {integratedResults.sources.map((source, index) => (
                        <li key={index}>{source}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="point-content">
      <h2>2. Presentación de la Empresa</h2>

      <div className="documents-section">
        <h4>1. DOCUMENTOS</h4>
        <h5>Documentos de la empresa</h5>

        <div className="upload-area">
          <div className="upload-box">
            {documents.length === 0 && (
            <p>Todavía no hay ningún archivo importado</p>
            )}
            
            <input
              type="file"
              onChange={handleFileUpload}
              accept=".docx,.pdf,.txt,.jpg,.png"
              className="file-input"
              id="file-upload"
              disabled={uploadLoading}
            />
            <label htmlFor="file-upload" className="upload-btn">
              {uploadLoading ? `${uploadStatus} (${uploadProgress}%)` : 'Importar'}
            </label>
          </div>
          <p className="file-formats">
            Formatos permitidos: docx, pdf, txt, jpg, png, gif | Tamaño máximo: 90MB
          </p>
        </div>

        {uploadLoading && (
          <div
            className="loading-indicator"
            style={{
              marginTop: '15px',
              textAlign: 'center',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
            }}
          >
            <div
              className="progress-bar"
              style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                marginBottom: '10px',
              }}
            >
              <div
                className="progress-fill"
                style={{
                  height: '100%',
                  width: `${uploadProgress}%`,
                  backgroundColor: '#28a745',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <span className="loading-text" style={{ fontSize: '14px', color: '#666' }}>
              {uploadStatus}
            </span>
          </div>
        )}

        {documents.length > 0 && (
          <div className="documents-list">
            <h5>Documentos subidos:</h5>
            {documents.map((doc, index) => (
              <div key={index} className="document-item">
                <span>📄 {doc.filename}</span>
                <button
                  onClick={() => handleDeleteDocument(doc.id)}
                  className="delete-btn"
                  style={{ marginLeft: '10px', color: 'red', border: 'none', background: 'none', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="web-search-section">
        <h4>2. BÚSQUEDA WEB</h4>
        <p className="section-subtitle">INFORMACIÓN DE LA EMPRESA PARA LA BÚSQUEDA.</p>

        <div className="form-grid">
          <div className="form-group">
            <label>Nombre de la empresa</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Nombre de la empresa"
            />
          </div>

          <div className="form-group">
            <label>Tamaño de la empresa</label>
            <select value={companySize} onChange={(e) => setCompanySize(e.target.value)}>
              <option value="">Seleccionar tamaño</option>
              {companySizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Sector/Industria</label>
            <select value={sector} onChange={(e) => setSector(e.target.value)}>
              <option value="">Seleccionar sector</option>
              {sectorOptions.map((sect) => (
                <option key={sect} value={sect}>
                  {sect}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Productos/Servicios</label>
            <input
              type="text"
              value={products}
              onChange={(e) => setProducts(e.target.value)}
              placeholder="Ej. Software, Consultoría"
            />
          </div>

          <div className="form-group">
            <label>Ubicación</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ej. Madrid"
            />
          </div>

          <div className="form-group">
            <label>Términos adicionales</label>
            <input
              type="text"
              value={additionalTerms}
              onChange={(e) => setAdditionalTerms(e.target.value)}
              placeholder="Ej. Innovación, exportación, premios..."
            />
          </div>
        </div>
      </div>

      <div className="analysis-type-section">
        <h4>TIPO DE ANÁLISIS.</h4>

        <div className="form-group">
          <label>Información general</label>
          <select value={analysisType} onChange={(e) => setAnalysisType(e.target.value)}>
            {analysisTypeOptions.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Ver/Editar prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={8}
            placeholder="Edita el prompt de análisis..."
          />
        </div>
      </div>

      <div className="mode-selection">
        <h4>3. MODO DE ANÁLISIS</h4>

        <div className="analysis-tabs">
          <div className="tab-buttons">
            {analysisTabOptions.map((tab, index) => (
              <button
                key={index}
                className={`tab-btn ${activeAnalysisTab === index ? 'active' : ''}`}
                onClick={() => setActiveAnalysisTab(index)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="tab-content">{renderAnalysisTabContent()}</div>
        </div>
      </div>
    </div>
  );
}

export default Point2;